/**
 * AssetUploadCard - Card for displaying an asset in the upload queue
 * Layout: Thumbnail left | Content right (title/desc above, tags below)
 */

import { useState, useCallback, useEffect, useRef } from "react";
import {
  Music,
  Video,
  Image as ImageIcon,
  Check,
  Loader2,
  AlertCircle,
  Trash2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronDown } from "lucide-react";
import { AssetTagEditor } from "./AssetTagEditor";
import type {
  UploadingAsset,
  AssetTags,
  AudioRole,
  VisualMotion,
} from "./types";
import { AUDIO_ROLE_OPTIONS, VISUAL_MOTION_OPTIONS } from "./types";
import { TAXONOMY_COLORS } from "@/components/audio/audioTypes";
import type { SceneWithShots } from "@/types/shots";
import "./AssetUploadCard.css";

interface AssetUploadCardProps {
  asset: UploadingAsset;
  scenes: SceneWithShots[];
  isSelected: boolean;
  onTitleChange: (title: string) => void;
  onTagsChange: (tags: AssetTags) => void;
  onRemove: () => void;
  onToggleSelect: () => void;
}

export function AssetUploadCard({
  asset,
  scenes,
  isSelected,
  onTitleChange,
  onTagsChange,
  onRemove,
  onToggleSelect,
}: AssetUploadCardProps) {
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isDeleteArmed, setIsDeleteArmed] = useState(false);
  const deleteButtonRef = useRef<HTMLButtonElement>(null);

  // Handle click outside to disarm delete
  useEffect(() => {
    if (!isDeleteArmed) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        deleteButtonRef.current &&
        !deleteButtonRef.current.contains(e.target as Node)
      ) {
        setIsDeleteArmed(false);
      }
    };

    // Auto-disarm after 3 seconds
    const timeoutId = setTimeout(() => setIsDeleteArmed(false), 3000);

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDeleteArmed]);

  // Handle delete button click - two-click confirmation
  const handleDeleteClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (isDeleteArmed) {
        // Second click - actually delete
        onRemove();
        setIsDeleteArmed(false);
      } else {
        // First click - arm the delete
        setIsDeleteArmed(true);
      }
    },
    [isDeleteArmed, onRemove],
  );

  // Get icon for asset type
  const TypeIcon =
    asset.type === "audio" ? Music : asset.type === "video" ? Video : ImageIcon;

  // Get file extension
  const fileExtension = asset.file.name.split(".").pop()?.toUpperCase() || "";

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Get asset type label
  const getTypeLabel = (): string => {
    switch (asset.type) {
      case "audio":
        return "Audio";
      case "video":
        return "Video";
      case "image":
        return "Image";
      default:
        return "File";
    }
  };

  const isAudio = asset.type === "audio";
  const isVisual = asset.type === "video" || asset.type === "image";

  // Handle role change (audio)
  const handleRoleChange = useCallback(
    (role: AudioRole) => {
      onTagsChange({ ...asset.tags, role });
    },
    [asset.tags, onTagsChange],
  );

  // Handle motion change (visual)
  const handleMotionChange = useCallback(
    (motion: VisualMotion) => {
      onTagsChange({ ...asset.tags, motion });
    },
    [asset.tags, onTagsChange],
  );

  // Status indicators
  const isUploading = asset.status === "uploading";
  const isComplete = asset.status === "complete";
  const hasError = asset.status === "error";
  const isPending = asset.status === "pending";

  // Handle title blur
  const handleTitleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      setIsEditingTitle(false);
      const value = e.target.value.trim();
      if (value && value !== asset.title) {
        onTitleChange(value);
      }
    },
    [asset.title, onTitleChange],
  );

  // Handle key press for inputs
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, onBlur: () => void) => {
      if (e.key === "Enter") {
        e.preventDefault();
        onBlur();
      }
      if (e.key === "Escape") {
        onBlur();
      }
    },
    [],
  );

  return (
    <div
      data-testid={`upload-row-${asset.id}`}
      data-status={asset.status}
      className={cn(
        "asset-upload-card",
        isSelected && "asset-upload-card--selected",
        isComplete && "asset-upload-card--complete",
        hasError && "asset-upload-card--error",
      )}
    >
      {/* Thumbnail / Preview */}
      <div className="asset-upload-card__thumbnail">
        {asset.thumbnail ? (
          <img
            src={asset.thumbnail}
            alt={asset.title}
            className="asset-upload-card__thumbnail-img"
          />
        ) : (
          <div className="asset-upload-card__thumbnail-placeholder">
            <TypeIcon className="w-8 h-8" />
          </div>
        )}

        {/* Status overlay */}
        {(isUploading || isComplete || hasError) && (
          <div
            data-testid="upload-status-badge"
            data-status={asset.status}
            className="asset-upload-card__status-overlay"
          >
            {isUploading && (
              <Loader2 className="w-6 h-6 animate-spin text-[var(--color-primitive-neutral-0)]" />
            )}
            {isComplete && (
              <div className="asset-upload-card__status-icon asset-upload-card__status-icon--success">
                <Check className="w-4 h-4" />
              </div>
            )}
            {hasError && (
              <div className="asset-upload-card__status-icon asset-upload-card__status-icon--error">
                <AlertCircle className="w-4 h-4" />
              </div>
            )}
          </div>
        )}

        {/* Selection checkbox - always visible */}
        <div className="asset-upload-card__checkbox">
          <Checkbox
            checked={isSelected}
            onCheckedChange={() => onToggleSelect()}
          />
        </div>

        {/* Type badge */}
        <div className="asset-upload-card__type-badge">{fileExtension}</div>
      </div>

      {/* Content */}
      <div className="asset-upload-card__content">
        {/* Header with title and delete button */}
        <div className="asset-upload-card__header">
          {/* Title */}
          {isEditingTitle ? (
            <input
              type="text"
              className="asset-upload-card__title-input"
              defaultValue={asset.title}
              onBlur={handleTitleBlur}
              onKeyDown={(e) =>
                handleKeyDown(e, () => (e.target as HTMLInputElement).blur())
              }
              autoFocus
            />
          ) : (
            <h4
              className="asset-upload-card__title"
              onClick={() => isPending && setIsEditingTitle(true)}
              onKeyDown={(e) => {
                if (isPending && (e.key === "Enter" || e.key === " ")) {
                  e.preventDefault();
                  setIsEditingTitle(true);
                }
              }}
              tabIndex={isPending ? 0 : -1}
              role="button"
              aria-label={`Edit title: ${asset.title}`}
              title={asset.title}
            >
              {asset.title}
            </h4>
          )}

          {/* Delete button - two-click confirmation */}
          {isPending && (
            <button
              ref={deleteButtonRef}
              type="button"
              data-testid="upload-remove-button"
              className={cn(
                "asset-upload-card__delete-btn",
                isDeleteArmed && "asset-upload-card__delete-btn--armed",
              )}
              onClick={handleDeleteClick}
              title={isDeleteArmed ? "Click again to delete" : "Delete asset"}
              aria-label={isDeleteArmed ? "Confirm delete" : "Delete asset"}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Metadata line: Size · Type · Role dropdown */}
        <div className="asset-upload-card__meta">
          <span>{formatFileSize(asset.file.size)}</span>
          <span className="asset-upload-card__meta-sep">·</span>
          <span>{getTypeLabel()}</span>
          <span className="asset-upload-card__meta-sep">·</span>

          {/* Role dropdown for audio */}
          {isAudio && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="asset-upload-card__role-trigger"
                  style={
                    {
                      "--role-color": TAXONOMY_COLORS.role.hex,
                    } as React.CSSProperties
                  }
                >
                  <span className="asset-upload-card__role-dot" />
                  <span>{asset.tags.role || "Role"}</span>
                  <ChevronDown className="w-3 h-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-28">
                {AUDIO_ROLE_OPTIONS.map((option) => (
                  <DropdownMenuItem
                    key={option.id}
                    onClick={() => handleRoleChange(option.id)}
                  >
                    {option.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Motion dropdown for visual */}
          {isVisual && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="asset-upload-card__role-trigger"
                  style={
                    {
                      "--role-color": TAXONOMY_COLORS.role.hex,
                    } as React.CSSProperties
                  }
                >
                  <span className="asset-upload-card__role-dot" />
                  <span>{asset.tags.motion || "Motion"}</span>
                  <ChevronDown className="w-3 h-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-28">
                {VISUAL_MOTION_OPTIONS.map((option) => (
                  <DropdownMenuItem
                    key={option.id}
                    onClick={() => handleMotionChange(option.id)}
                  >
                    {option.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {/* Error message */}
        {hasError && asset.error && (
          <p className="asset-upload-card__error">{asset.error}</p>
        )}

        {/* Divider */}
        <div className="asset-upload-card__divider" />

        {/* Tags row */}
        {isPending && (
          <div className="asset-upload-card__tags-row">
            <AssetTagEditor
              tags={asset.tags}
              scenes={scenes}
              onChange={onTagsChange}
            />
          </div>
        )}
      </div>
    </div>
  );
}
